import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Getunits } from "../store/slices/settings";
import { Listitems } from "../store/slices/items";
import { Getsingledetail,GetCurrentStock } from "../store/slices/sale";
import ProductSelector from "../common/ProductSelector";
import ItemRow from "../common/ItemRow";
import AddItemPopUp from "../common/AddItemPopUp";

const InvoiceSecond = ({ onChildDataChange, onSubmit }) => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));
  const id = user?.data?.profile_id;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [state, setState] = useState({
    units: [],
    itemList: [],
    singleDetail: {},
    selectedProduct: null,
    quantity: 1,
    unit_id: null,
    discount: 0,
    price: 0,
    discount_type: "Fixed",
    shippingCost: 0,
  shippingTaxPercent: 0,
  shippingTaxType: "excluding",
  shippingTaxAmount: 0,
    addedItems: [],
    tax: 0,
    price_tax_type: "Excluding Tax",
    subtotal: 0,
    taxAmounts: {},
    hsn: null,
    currentStock: null,
    shippingGst: 0,
    roundoff: 0,
    roundedGrandTotal: 0,
  });

  const fetchUnits = useCallback(async () => {
    try {
      const data = await dispatch(Getunits()).unwrap();
      setState((prevState) => ({ ...prevState, units: data?.data }));
    } catch (error) {
      console.log(error.message);
    }
  }, [dispatch]);

  const fetchItemList = useCallback(async () => {
    try {
      const data = await dispatch(Listitems({ profile_id: id })).unwrap();
      setState((prevState) => ({ ...prevState, itemList: data?.data }));
    } catch (error) {
      console.error(error.message);
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchUnits();
    fetchItemList();
  }, [fetchUnits, fetchItemList]);

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const handleOpenModal = () => {
    setIsModalOpen(true); // Open the modal
  };


  const handleProductChange = useCallback(
  async (productId) => {
    const normalizedProductId = productId ? Number(productId) : null;
    const listProductData = state?.itemList?.find(
      (item) => Number(item?.id) === normalizedProductId
    );
    setState((prevState) => ({
      ...prevState,
      selectedProduct: normalizedProductId,
    }));

    if (normalizedProductId) {
      try {
        const [dataResult, stockResult] = await Promise.allSettled([
          dispatch(
            Getsingledetail({ profile_id: id, item_id: normalizedProductId })
          ).unwrap(),
          dispatch(
            GetCurrentStock({ profile_id: id, item_id: normalizedProductId })
          ).unwrap(),
        ]);

        const data = dataResult.status === 'fulfilled' ? dataResult.value : null;
        const stockData = stockResult.status === 'fulfilled' ? stockResult.value : null;

        const currentStock =
          stockData?.data?.opening_stock_qty ??
          stockData?.opening_stock_qty ??
          stockData?.data?.current_stock ??
          stockData?.current_stock ??
          0;

        const selectedProductData = Array.isArray(data?.data)
          ? data?.data?.[0]
          : data?.data;
        const productData = selectedProductData || listProductData || {};

        setState((prevState) => ({
          ...prevState,
          price: Number(productData?.sale_price ?? productData?.purchase_price ?? 0)?.toFixed(2),
          singleDetail: productData,
          hsn: productData?.hsn || productData?.hsn_sac || productData?.hsn_code || '',
          unit_id: productData?.unit || "",
          tax: productData?.tax || 0,
          price_tax_type:
            productData?.sale_price_tax_type || "Excluding Tax",
          discount_type: productData?.discount_type || "Fixed",
          discount: Number(productData?.discount).toFixed(2) || 0,
          currentStock,
        }));
      } catch (error) {
        console.error(error.message);
        setState((prevState) => ({
          ...prevState,
          currentStock: null,
        }));
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        singleDetail: {},
        hsn: '',
        unit_id: "",
        tax: 0,
        price_tax_type: "Excluding Tax",
        currentStock: null,
      }));
    }
  },
  [dispatch, id, state?.itemList]
);

  const handleInputChange = (field, value) => {
    // Special handling for shipping-related fields so we can compute shipping tax
    const f = String(field || "").toLowerCase();
    if (f.includes("shipping")) {
      // Accept various naming conventions: shippingCost, shipping_cost, shippingTax, shipping_tax_percent, shipping_tax_type, etc.
      const shippingCost =
        f === "shippingcost" || f === "shipping_cost"
          ? parseFloat(value) || 0
          : parseFloat(state.shippingCost) || 0;

      const shippingTaxPercent =
        f === "shippingtax" || f === "shipping_tax" || f === "shippingtaxpercent" || f === "shipping_tax_percent"
          ? parseFloat(value) || 0
          : parseFloat(state.shippingTaxPercent) || 0;

      const shippingTaxType =
        f === "shippingtaxtype" || f === "shipping_tax_type"
          ? value
          : state.shippingTaxType || "excluding";

      // Calculate shipping tax amount depending on whether tax is included or excluded
      let shippingTaxAmount = 0;
      if (shippingTaxPercent > 0) {
        if (shippingTaxType === "including") {
          // shippingCost already includes tax; extract tax portion
          shippingTaxAmount = parseFloat(
            ((shippingCost * shippingTaxPercent) / (100 + shippingTaxPercent)).toFixed(2)
          );
        } else {
          // tax to be added on top of shippingCost
          shippingTaxAmount = parseFloat(
            ((shippingCost * shippingTaxPercent) / 100).toFixed(2)
          );
        }
      }

      // Update taxAmounts: remove previous shipping tax entry and add new
      const updatedTaxAmounts = { ...state.taxAmounts };
      const prevRate = parseFloat(state.shippingTaxPercent) || 0;
      const prevAmount = parseFloat(state.shippingTaxAmount) || 0;
      if (prevRate && updatedTaxAmounts[prevRate]) {
        updatedTaxAmounts[prevRate] = parseFloat((updatedTaxAmounts[prevRate] - prevAmount).toFixed(2));
        if (updatedTaxAmounts[prevRate] <= 0) delete updatedTaxAmounts[prevRate];
      }

      if (shippingTaxPercent) {
        if (updatedTaxAmounts[shippingTaxPercent]) {
          updatedTaxAmounts[shippingTaxPercent] = parseFloat(
            (updatedTaxAmounts[shippingTaxPercent] + shippingTaxAmount).toFixed(2)
          );
        } else {
          updatedTaxAmounts[shippingTaxPercent] = parseFloat(shippingTaxAmount.toFixed(2));
        }
      }

      setState((prevState) => ({
        ...prevState,
        shippingCost,
        shippingTaxPercent,
        shippingTaxType,
        shippingTaxAmount,
        taxAmounts: updatedTaxAmounts,
      }));
    } else {
      // For other fields, update state normally
      setState((prevState) => ({ ...prevState, [field]: value }));
    }
  };

  const calculateTotal = useMemo(() => {
     const { quantity, discount, discount_type, tax, price_tax_type } = state;
    const price = Number(state.price) || 0;
    const quantityTotal = price * quantity || 0;

    const taxRate = Number(tax) || 0;
    const discountValue = Number(discount) || 0;

    let taxAmount = 0;
    let discountAmount = 0;
    let totalBeforeTax = quantityTotal;
    let preTaxTotal = quantityTotal;

    if (price_tax_type === "Including Tax") {
      // Extract pre-tax amount
      taxAmount = +((quantityTotal * taxRate) / (100 + taxRate)).toFixed(2);
      preTaxTotal = quantityTotal - taxAmount;

      // Apply discount
      if (discount_type === "Fixed") {
        discountAmount = Math.min(discountValue, quantityTotal);
      } else if (discount_type === "Percentage") {
        discountAmount = (preTaxTotal * discountValue) / 100;
      }

      // Total after discount
      totalBeforeTax =
        discountValue > 0
          ? discount_type === "Fixed"
            ? quantityTotal - discountAmount
            : preTaxTotal - discountAmount
          : quantityTotal;

      // Recalculate tax based on discounted price
      if (discountValue > 0) {
        taxAmount = +((totalBeforeTax * taxRate) / (100 + taxRate)).toFixed(2);
      }
    } else {
      // Excluding tax
      if (discount_type === "Fixed") {
        discountAmount = Math.min(discountValue, totalBeforeTax);
      } else if (discount_type === "Percentage") {
        discountAmount = (totalBeforeTax * discountValue) / 100;
      }

      totalBeforeTax -= discountAmount;
      taxAmount = +(totalBeforeTax * (taxRate / 100)).toFixed(2);
    }

    // Final total logic
    const finalTotal =
      price_tax_type === "Including Tax"
        ? +totalBeforeTax.toFixed(2) // Already includes tax
        : +(totalBeforeTax + taxAmount).toFixed(2);

    return {
      finalTotal,
      discountedTotal: totalBeforeTax,
      taxAmount,
      subtotal:
        price_tax_type === "Including Tax"
          ? +(totalBeforeTax - taxAmount).toFixed(2)
          : totalBeforeTax,
      discountAmount,
    };
 }, [state]);

  const handleAddItem = () => {
    const {
      selectedProduct,
      quantity,
      price,
      unit_id,
      singleDetail,
      price_tax_type,
      tax,
      discount,
      discount_type,
    } = state;

    if (!selectedProduct || quantity <= 0 || Number(price) <= 0) {
      toast.error("Please fill out all fields correctly.");
      return;
    }
    // Find the unit name based on the unit_id
    const unitName =
      state?.units?.find((unit) => unit.id === unit_id)?.unit || "";

    // Calculate the total for the current item
    const newItem = {
      hsn: state.hsn || singleDetail?.hsn || singleDetail?.hsn_sac || singleDetail?.hsn_code || '',
      item_id: state.selectedProduct,
      quantity: state.quantity,
      unit_id: unit_id,
      unit_name: unitName, // Use the unitName here
      price: state.price,
      price_tax_type: price_tax_type,
      tax: tax,
      tax_type: "GST",
      tax_amount: calculateTotal.taxAmount.toFixed(2),
      discount: discount,
      discount_type: discount_type,
      sub_total: calculateTotal.subtotal.toFixed(2),
      total_amount: calculateTotal.finalTotal.toFixed(2),
    };

    // Calculate the tax amount for this item
    const recalculatedTax = calculateTotal.taxAmount;

    // Copy the existing taxAmounts state
    const newTaxAmounts = { ...state.taxAmounts };

    // Update the taxAmounts state for the specific GST rate (assuming GST is stored as the tax value)
    const currentTaxRate = parseFloat(tax);
    if (currentTaxRate) {
      if (newTaxAmounts[currentTaxRate]) {
        newTaxAmounts[currentTaxRate] += recalculatedTax; // Add to existing tax amount for that rate
      } else {
        newTaxAmounts[currentTaxRate] = recalculatedTax; // Initialize new tax amount for that rate
      }
    }

    // Update the state with the new item and updated tax amounts
    setState((prevState) => ({
      ...prevState,
      addedItems: [...prevState.addedItems, newItem],
      taxAmounts: newTaxAmounts,
    }));

    clearInputs(); // Clear the form inputs for the next item
  };

  const clearInputs = () => {
    setState((prevState) => ({
      ...prevState,
      selectedProduct: null,
      quantity: 1,
      discount: 0,
      discount_type: "Fixed",
      unit_id: "",
      tax: 0,
      price_tax_type: "Excluding Tax",
      singleDetail: {},
      hsn: '',
      price: 0,
      currentStock: null,
    }));
  };

  const handleItemChange = useCallback(
  (field, value, index) => {
    const updatedItems = [...state.addedItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    const item = updatedItems[index];
    const itemPrice = parseFloat(item.price) || 0;
    const itemQuantity = parseFloat(item.quantity) || 0;
    const itemTotal = itemPrice * itemQuantity;

    const gstRate = parseFloat(field === "tax" ? value : item.tax) || 0;
    const discountValue = parseFloat(item.discount) || 0;
    const discountType = item.discount_type;
    const priceTaxType = item.price_tax_type;

    const calculateDiscount = (total, discountValue, type) => {
      if (type === "Percentage") return (total * discountValue) / 100;
      if (type === "Fixed") return Math.min(discountValue, total);
      return 0;
    };

    const calculateTax = (total, gst, taxIncluded) => {
      if (taxIncluded) return (total * gst) / (100 + gst);
      return (total * gst) / 100;
    };

    // Calculate discounted total
    let discountAmount = calculateDiscount(itemTotal, discountValue, discountType);
    let totalAfterDiscount = itemTotal - discountAmount;

    // Calculate tax
    let taxAmount = calculateTax(totalAfterDiscount, gstRate, priceTaxType === "Including Tax");

    // Determine final total and subtotal
    let finalTotal, subtotal;
    if (priceTaxType === "Including Tax") {
      finalTotal = totalAfterDiscount; // Already includes tax
      subtotal = totalAfterDiscount - taxAmount;
    } else {
      finalTotal = totalAfterDiscount + taxAmount; // Add GST for final total
      subtotal = totalAfterDiscount; // Pre-tax subtotal
    }

    updatedItems[index].total_amount = finalTotal.toFixed(2);
    updatedItems[index].sub_total = subtotal.toFixed(2);
    updatedItems[index].tax_amount = taxAmount.toFixed(2);
    updatedItems[index].discountAmount = discountAmount.toFixed(2);

    // Recalculate total tax amounts for all GST rates
    const updatedTaxAmounts = {};
    updatedItems.forEach((itm) => {
      const itmGst = parseFloat(itm.tax) || 0;
      if (itmGst > 0) {
        const itmTotal = parseFloat(itm.price) * parseFloat(itm.quantity) || 0;
        const itmDiscount = calculateDiscount(itmTotal, parseFloat(itm.discount) || 0, itm.discount_type);
        const itmAfterDiscount = itmTotal - itmDiscount;
        const itmTax = calculateTax(itmAfterDiscount, itmGst, itm.price_tax_type === "Including Tax");
        if (updatedTaxAmounts[itmGst]) {
          updatedTaxAmounts[itmGst] += itmTax;
        } else {
          updatedTaxAmounts[itmGst] = itmTax;
        }
      }
    });

    // Round tax amounts to 2 decimals
    Object.keys(updatedTaxAmounts).forEach((key) => {
      updatedTaxAmounts[key] = parseFloat(updatedTaxAmounts[key].toFixed(2));
    });

    setState((prevState) => ({
      ...prevState,
      addedItems: updatedItems,
      taxAmounts: updatedTaxAmounts,
    }));
  },
    [state.addedItems, state.taxAmounts]
  );

  const handleRemoveItem = useCallback(
    (index) => {
      const updatedItems = [...state.addedItems];
      const item = updatedItems[index];

      // Recalculate the tax for the item being removed
      const gst = parseFloat(item.tax) || 0;
      const price = parseFloat(item.price) || 0;
      const itemQuantity = parseFloat(item.quantity) || 0;
      let taxAmount = 0;

      // Tax calculation based on the item's price type
      if (item.price_tax_type === "Excluding Tax") {
        taxAmount = (price * itemQuantity * gst) / 100;
      } else if (item.price_tax_type === "Including Tax") {
        const totalWithTax = price * itemQuantity;
        taxAmount = (totalWithTax * gst) / (100 + gst);
      }

      // Update tax amounts
      const newTaxAmounts = { ...state.taxAmounts };
      if (newTaxAmounts[gst]) {
        newTaxAmounts[gst] -= taxAmount;
        if (newTaxAmounts[gst] < 0) {
          newTaxAmounts[gst] = 0; // Ensure no negative tax amounts
        }
      }

      // Remove the item from addedItems
      updatedItems.splice(index, 1);

      // Update the state with the new addedItems and tax amounts
      setState((prevState) => ({
        ...prevState,
        addedItems: updatedItems,
        taxAmounts: newTaxAmounts,
      }));
    },
    [state.addedItems, state.taxAmounts]
  );

  const grandTotal = useMemo(() => {
    // Calculate total of all items and shipping
    const itemsTotal = state.addedItems.reduce(
      (sum, item) => sum + Number(item.total_amount),
      0
    );
    const shipping = Number(state.shippingCost) || 0;
    const shippingTaxPercent = Number(state.shippingTaxPercent) || 0;
    const shippingTaxType = state.shippingTaxType || "excluding";

    let shippingTaxAmount = 0;
    if (shippingTaxPercent > 0) {
      if (shippingTaxType === "including") {
        // shipping already includes tax; extract portion
        shippingTaxAmount = parseFloat(
          ((shipping * shippingTaxPercent) / (100 + shippingTaxPercent)).toFixed(2)
        );
      } else {
        // tax to be added on top
        shippingTaxAmount = parseFloat(((shipping * shippingTaxPercent) / 100).toFixed(2));
      }
    }

    // Final grand total: if shipping tax is excluding, add tax amount on top; if including, shipping already has tax
    const final = itemsTotal + shipping + (shippingTaxType === "excluding" ? shippingTaxAmount : 0);
    return final.toFixed(2);
  }, [state.addedItems, state.shippingCost, state.shippingTaxPercent, state.shippingTaxType]);

  useEffect(() => {
    const discountAmount = state?.addedItems
      ?.reduce((sum, item) => {
        const discountAmountSubTotal = isNaN(Number(item.discount_amount))
          ? 0
          : Number(item.discount_amount);
        return sum + discountAmountSubTotal;
      }, 0)
      ?.toFixed(2);

    // Total GST should include: GST from items + GST on shipping (if any)
    const itemsGstTotal = state?.addedItems
      ?.reduce((sum, item) => {
        const itemTax = isNaN(Number(item.tax_amount))
          ? 0
          : Number(item.tax_amount);
        return sum + itemTax;
      }, 0) || 0;

    const gstTotal = (itemsGstTotal).toFixed(2);
    console.log("grandTotal", grandTotal);
    // Calculate the rounded grand total
    const roundedGrandTotal = Math.round(grandTotal);
    console.log("roundedGrandTotal", roundedGrandTotal);

    // Calculate round-off value
    let roundoff = 1 - (grandTotal - roundedGrandTotal);

    if (roundoff > 0.5) {
      roundoff = (1 - roundoff) * -1;
    }
    console.log("roundoff", roundoff);

    setState((prevState) => ({
      ...prevState,
      roundedGrandTotal,
      roundoff: roundoff.toFixed(2),
    }));

    const invoiceData = {
      sub_total: state?.addedItems
        ?.reduce((sum, item) => sum + Number(item.sub_total), 0)
        .toFixed(2),
      shipping_cost: Number(state?.shippingCost)?.toFixed(2),
      shipping_tax_percent: Number(state?.shippingTaxPercent)?.toFixed(2),
      shipping_tax_type: state?.shippingTaxType || null,
      shipping_tax_amount: Number(state?.shippingTaxAmount)?.toFixed(2),
      discount_amount: discountAmount,
      grand_total: roundedGrandTotal.toFixed(2), // Use the rounded grand total
      balance_amount: roundedGrandTotal.toFixed(2), // Balance also follows rounded total
      total_gst: gstTotal,
      roundoff: roundoff.toFixed(2), // Ensure roundoff is formatted correctly
      invoice_items: state?.addedItems?.map((item) => ({
        item_id: item.item_id,
        quantity: item.quantity,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        price: item.price,
        price_tax_type: item.price_tax_type,
        tax_amount: item.tax_amount,
        tax: item.tax,
        tax_type: "GST",
        discount: item.discount,
        discount_type: item.discount_type,
        sub_total: item.sub_total,
        total_amount: item.total_amount,
      })),
    };

    onChildDataChange(invoiceData);
  }, [state.addedItems, state.shippingCost, state.shippingTaxAmount, grandTotal, onChildDataChange]);
  // console.log(state)
  return (
    <>
      {isModalOpen && (
        <AddItemPopUp
          show={isModalOpen}
          onClose={handleCloseModal}
          onCategoryAdded={fetchItemList}
        />
      )}
      <div className="row my-3">
        <div className="col-md-12">
          <div className="card custom-card">
            <div className="card-body">
              <div className="d-flex justify-content-end mb-2">
                <button
                  className="btn ripple btn-default"
                  onClick={handleOpenModal}
                >
                  Add Item
                </button>
              </div>
              <table className="table item-table">
                <ProductSelector
                  handleProductChange={handleProductChange}
                  singleDetail={state?.singleDetail}
                  handleInputChange={handleInputChange}
                  state={state}
                  calculateTotal={calculateTotal}
                  handleAddItem={handleAddItem}
                  isDiscount={true}
                />
                <tbody>
                  <ItemRow
                    addedItems={state?.addedItems}
                    handleAddItem={handleAddItem}
                    handleInputChange={handleInputChange}
                    grandTotal={grandTotal}
                    state={state}
                    handleItemChange={handleItemChange}
                    handleRemoveItem={handleRemoveItem}
                    isDiscount={true}
                    isSale={true}
                  />
                </tbody>
                <tr>
                  <td colspan="10" className="text-right align-middle">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={onSubmit}
                    >
                      Generate Invoice
                    </button>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceSecond;
